import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Local type definitions until shared package is ready
interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: 'ADMIN' | 'DOCTOR' | 'NURSE' | 'RECEPTIONIST' | 'BILLING_STAFF' | 'LAB_TECHNICIAN' | 'PATIENT';
  isActive: boolean;
  lastLoginAt?: string | null;
  createdAt: string;
  updatedAt: string;
  doctor?: {
    id: string;
    licenseNumber: string;
    specialization: string;
    department: string;
  };
  patient?: {
    id: string;
    patientNumber: string;
  };
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true;
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string; refreshToken: string }>) => {
      // Ensure dates are serialized as strings
      const user = {
        ...action.payload.user,
        createdAt: typeof action.payload.user.createdAt === 'string' 
          ? action.payload.user.createdAt 
          : new Date(action.payload.user.createdAt).toISOString(),
        updatedAt: typeof action.payload.user.updatedAt === 'string' 
          ? action.payload.user.updatedAt 
          : new Date(action.payload.user.updatedAt).toISOString(),
      };
      
      state.user = user;
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
      state.isAuthenticated = true;
      state.isLoading = false;
    },
    loginFailure: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        const updates = { ...action.payload };
        
        // Ensure any date fields are properly serialized
        if (updates.createdAt && typeof updates.createdAt !== 'string') {
          updates.createdAt = new Date(updates.createdAt).toISOString();
        }
        if (updates.updatedAt && typeof updates.updatedAt !== 'string') {
          updates.updatedAt = new Date(updates.updatedAt).toISOString();
        }
        
        state.user = { ...state.user, ...updates };
      }
    },
    refreshToken: (state, action: PayloadAction<{ token: string; refreshToken: string }>) => {
      state.token = action.payload.token;
      state.refreshToken = action.payload.refreshToken;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  refreshToken,
} = authSlice.actions;

export default authSlice.reducer; 